import { Box, Typography } from '@mui/material';

import { COLORS } from '../../../IndividualPlan/tokens';
import { buildLtelCountBarData, type LtelBarRow } from './ltelCountBarData';
import { shortSchoolYearLabel } from '../../utils/schoolYear';
import type { PeLtelYear } from '../../types';

const CONTINUING_COLOR = COLORS.navy;
const NEWLY_ENTERED_COLOR = COLORS.orange;
const MAX_BAR_HEIGHT = 135;
const BAR_WIDTH = 56;

const LEGEND_ITEMS = [
  { label: 'Continuing', color: CONTINUING_COLOR },
  { label: 'Newly entered this year', color: NEWLY_ENTERED_COLOR },
];

function BarStack({ row, isCurrentYear }: { row: LtelBarRow; isCurrentYear: boolean }) {
  if (row.newlyEntered == null) {
    return (
      <Box
        data-segment="continuing"
        aria-label={`LTEL students: ${row.count}`}
        style={{ flexGrow: 1, flexBasis: 0, backgroundColor: CONTINUING_COLOR }}
      />
    );
  }

  return (
    <>
      {row.newlyEntered > 0 && (
        <Box
          data-segment="newly-entered"
          {...(isCurrentYear ? { 'data-bar-newly-current': '' } : {})}
          data-school-year={row.schoolYear}
          aria-label={`Newly entered: ${row.newlyEntered}`}
          style={{
            flexGrow: row.newlyEntered,
            flexBasis: 0,
            backgroundColor: NEWLY_ENTERED_COLOR,
          }}
        />
      )}
      {row.continuing > 0 && (
        <Box
          data-segment="continuing"
          aria-label={`Continuing: ${row.continuing}`}
          style={{ flexGrow: row.continuing, flexBasis: 0, backgroundColor: CONTINUING_COLOR }}
        />
      )}
    </>
  );
}

export function LtelCountBar({ years }: { years: PeLtelYear[] }) {
  const rows = buildLtelCountBarData(years);

  if (rows.length === 0) {
    return <Box data-testid="ltel-count-bar-empty" />;
  }

  const maxCount = Math.max(...rows.map((row) => row.count), 1);

  return (
    <Box data-testid="ltel-count-bar" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography
          component="h4"
          sx={{ color: COLORS.muted, fontSize: 16, lineHeight: '24px', fontWeight: 700 }}
        >
          LTEL Count
        </Typography>
        <Box style={{ flexWrap: 'wrap' }} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {LEGEND_ITEMS.map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box
                style={{ backgroundColor: color }}
                sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0 }}
              />
              <Typography sx={{ fontSize: 16, lineHeight: '24px', color: COLORS.ink }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ position: 'relative', width: 'fit-content', minWidth: '100%' }}>
          <Box
            data-testid="ltel-baseline"
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 24,
              height: '1px',
              backgroundColor: COLORS.divider,
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 8 }}>
            {rows.map((row, index) => (
              <Box
                key={row.schoolYear}
                data-bar-column=""
                data-school-year={row.schoolYear}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: 1,
                  width: `${BAR_WIDTH}px`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: '16px',
                    fontWeight: 700,
                    textAlign: 'center',
                    color: COLORS.ink,
                  }}
                >
                  {row.count}
                </Typography>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column' }}
                  style={{ height: `${(row.count / maxCount) * MAX_BAR_HEIGHT}px` }}
                >
                  <BarStack row={row} isCurrentYear={index === rows.length - 1} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: '16px',
                    textAlign: 'center',
                    color: COLORS.muted,
                  }}
                >
                  {shortSchoolYearLabel(row.schoolYear)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
